import type { TFunction } from 'i18next';
import { FILTER_ALL, isCanonicalCategorySlug, normalizeStoredCategoryToSlug } from '../constants/categories';

function humanizeSlug(slug: string): string {
  return slug
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Display label for a category slug or legacy Arabic value.
 * Unknown legacy values map to miscellaneous for a stable non-Arabic UI in EN mode.
 */
export function translateCategoryName(name: string | undefined | null, t: TFunction): string {
  const s = String(name ?? '').trim();
  if (!s) return t('categories.miscellaneous');

  const normalized = normalizeStoredCategoryToSlug(s);
  if (normalized === FILTER_ALL) return t('categories.all');

  let slug =
    normalized ||
    (isCanonicalCategorySlug(s) ? s.trim().toLowerCase() : null);

  if (!slug) return s;

  const key = `categories.${slug}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return humanizeSlug(slug);
}

export const SYNC_MARKER = "OPEN_AUCTION_v1_FINAL_SYNC_OK_2026";

/**
 * Label for a category tree node from site settings (EN: i18n → nameEn → humanizeSlug(id) before Arabic name → translate).
 */
export function formatCategoryTreeLabel(
  node: { id: string; name?: string; nameEn?: string },
  lng: string,
  t: TFunction
): string {
  const id = String(node?.id || '').trim();
  if (!id) return '';

  const base = String(lng || '').split('-')[0].toLowerCase();

  // Prioritize nameEn or humanized ID in English mode BEFORE checking the dictionary (t)
  // because the dictionary might contain stale Arabic fallbacks for these keys.
  if (base === 'en') {
    if (node.nameEn && String(node.nameEn).trim()) return String(node.nameEn).trim();
    return humanizeSlug(id);
  }

  const slugKey = `categories.${id.toLowerCase()}`;
  const builtin = t(slugKey);
  if (builtin !== slugKey) return builtin;

  if (node.name && String(node.name).trim()) return String(node.name).trim();

  return translateCategoryName(id, t);
}
